import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Loader2, SlidersHorizontal, Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import type { Driver15Data, FnpcDecDecreaseRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';

const numCls = 'w-28 text-right bg-white text-[#1B365D] border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500 tabular-nums';
const txtCls = 'w-full min-w-[6rem] bg-white border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500';

const fmtE = (n: number) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n) + ' €';
const fmtPct = (n: number) =>
  new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + '%';

type TabKey = 'flagged' | 'all';

const emptyFnpcDecRow = (): FnpcDecDecreaseRow => ({
  matr_global_advisor: '',
  nome_global_advisor: '',
  clone_uo:            '',
  descrizione:         '',
  fnpc_nov:            0,
  fnpc_dec:            0,
  fnpc_jan:            0,
  delta_nov_dec_pct:   0,
  ratio_jan_dec_pct:   0,
});

const DecrementoFlussoNettoContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver15Data>(reportId, 15);

  const [data,       setData]       = useState<Driver15Data | null>(null);
  const [savedData,  setSavedData]  = useState<Driver15Data | null>(null);
  const [minRatio,   setMinRatio]   = useState<number>(100);
  const [maxDelta,   setMaxDelta]   = useState<number>(0);
  const [minFnpc,    setMinFnpc]    = useState<number>(300000);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isEditing,  setIsEditing]  = useState(false);
  const [tab,        setTab]        = useState<TabKey>('flagged');
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /** Aggiorna un campo NUMERICO di una riga (identificata da matr_global_advisor)
   *  in source_rows e flagged_rows. Editing libero: nessun ricalcolo lato client. */
  const updateRow = (matr: string, key: keyof FnpcDecDecreaseRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: FnpcDecDecreaseRow[]) => rs.map(r => r.matr_global_advisor === matr ? { ...r, [key]: val } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  /** Aggiorna un campo TESTUALE di una riga (per matr_global_advisor). Il matcher
   *  usa il valore corrente della chiave, così anche l'edit della matricola stessa
   *  resta coerente keystroke per keystroke. */
  const updateRowText = (matr: string, key: keyof FnpcDecDecreaseRow, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: FnpcDecDecreaseRow[]) => rs.map(r => r.matr_global_advisor === matr ? { ...r, [key]: value } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  /** Appende una riga vuota all'array sottostante del tab corrente. */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      return tab === 'flagged'
        ? { ...prev, flagged_rows: [...prev.flagged_rows, emptyFnpcDecRow()] }
        : { ...prev, source_rows: [...prev.source_rows, emptyFnpcDecRow()] };
    });
  };

  useEffect(() => {
    if (!remoteData) return;
    // Il BE puo' restituire rows/flagged_rows come null; normalizzo a array
    // vuoto per evitare crash su .length / spread.
    const normalized: Driver15Data = {
      ...remoteData,
      source_rows:  remoteData.source_rows  ?? [],
      flagged_rows: remoteData.flagged_rows ?? [],
    };
    setData(normalized);
    setSavedData(normalized);
    if (!initializedRef.current) {
      setMinRatio(remoteData.thresholds.min_ratio_jan_dec_pct);
      setMaxDelta(remoteData.thresholds.max_delta_nov_dec_pct);
      setMinFnpc(remoteData.thresholds.min_fnpc_dec);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave soglie (~600ms debounced). Attivo solo fuori editing.
  useEffect(() => {
    if (!data || !savedData || isReadOnly || isEditing) return;
    const st = savedData.thresholds;
    if (
      minRatio === st.min_ratio_jan_dec_pct &&
      maxDelta === st.max_delta_nov_dec_pct &&
      minFnpc  === st.min_fnpc_dec
    ) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver15Data = {
        ...data,
        thresholds: {
          min_ratio_jan_dec_pct: minRatio,
          max_delta_nov_dec_pct: maxDelta,
          min_fnpc_dec:          minFnpc,
        },
      };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave soglie Driver 15 fallito:', err);
        setMinRatio(st.min_ratio_jan_dec_pct);
        setMaxDelta(st.max_delta_nov_dec_pct);
        setMinFnpc(st.min_fnpc_dec);
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [minRatio, maxDelta, minFnpc, savedData, isReadOnly, isEditing, data, save]);

  const rows: FnpcDecDecreaseRow[] = useMemo(() => {
    if (!data) return [];
    const src = tab === 'flagged' ? data.flagged_rows : data.source_rows;
    return [...src].sort((a, b) => b.ratio_jan_dec_pct - a.ratio_jan_dec_pct);
  }, [data, tab]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  return (
    <div className="p-6 space-y-5">

      {/* Banner Investigation + Edit bar */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <RefreshCw className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Investigation</span> · Analisi dei Global Advisor con incremento sostanziale del Flusso netto di risparmio gestito, Certificates e Obbligazioni di terzi tra dicembre e gennaio successivamente a un decremento tra novembre e dicembre. La ratio sottostante tale investigation risiede nell'investigare eventuali politiche di posticipazione degli investimenti dei clienti dall'anno oggetto di indagine all'anno successivo da parte dei Gestori Global Advisor, al fine di rimandare eventuali obiettivi di raggiungimento del premio.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver15')}
          fileName={`Decremento_FNPC_Dic_Gen_${data.period}`}
        />
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* Soglie */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800">Ratio min Jan/Dec</p>
            <p className="text-[10px] text-gray-400 mt-0.5">default 100%</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {autoSaving && <Loader2 className="w-3.5 h-3.5 text-amber-700/60 animate-spin" />}
            <input
              type="number"
              min={0}
              value={minRatio}
              disabled={isReadOnly || isEditing}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setMinRatio(Math.max(0, v));
              }}
              className="w-16 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-400">%</span>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800">Δ max Nov→Dic</p>
            <p className="text-[10px] text-gray-400 mt-0.5">default 0% (dec ≤ nov)</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <input
              type="number"
              value={maxDelta}
              disabled={isReadOnly || isEditing}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setMaxDelta(v);
              }}
              className="w-16 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-400">%</span>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800">FNPC Dic min</p>
            <p className="text-[10px] text-gray-400 mt-0.5">default 300.000 €</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <input
              type="number"
              min={0}
              step={10000}
              value={minFnpc}
              disabled={isReadOnly || isEditing}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setMinFnpc(Math.max(0, v));
              }}
              className="w-24 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-400">€</span>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-700">{data.flagged_rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Global Advisor flaggati</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{data.source_rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Global Advisor analizzati</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{minRatio}%</div>
          <div className="text-[11px] text-gray-400 mt-1">Ratio min applicato</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setTab('flagged')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'flagged' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Solo flaggati
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            {data.flagged_rows.length}
          </span>
        </button>
        <button
          onClick={() => setTab('all')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'all' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tutti gli analizzati
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
            {data.source_rows.length}
          </span>
        </button>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
          Nessun Global Advisor in questa categoria.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                  <th className="text-left  px-4 py-2.5 font-semibold">Matr. GA</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Nome Global Advisor</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                  <th className="text-right px-4 py-2.5 font-semibold">FNPC Nov</th>
                  <th className="text-right px-4 py-2.5 font-semibold">FNPC Dic</th>
                  <th className="text-right px-4 py-2.5 font-semibold">FNPC Gen</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Δ Nov→Dic</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Ratio Gen/Dic</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.01 }}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.matr_global_advisor} onChange={e => updateRowText(row.matr_global_advisor, 'matr_global_advisor', e.target.value)} /> : row.matr_global_advisor}
                    </td>
                    <td className="px-4 py-3 text-[#1B365D] whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.nome_global_advisor} onChange={e => updateRowText(row.matr_global_advisor, 'nome_global_advisor', e.target.value)} /> : row.nome_global_advisor}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap" title={`CloneUo: ${row.clone_uo}`}>
                      {isEditing ? <input type="text" className={txtCls} value={row.descrizione} onChange={e => updateRowText(row.matr_global_advisor, 'descrizione', e.target.value)} /> : row.descrizione}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.fnpc_nov} onChange={e => updateRow(row.matr_global_advisor, 'fnpc_nov', e.target.value)} />
                        : fmtE(row.fnpc_nov)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-semibold tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.fnpc_dec} onChange={e => updateRow(row.matr_global_advisor, 'fnpc_dec', e.target.value)} />
                        : fmtE(row.fnpc_dec)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.fnpc_jan} onChange={e => updateRow(row.matr_global_advisor, 'fnpc_jan', e.target.value)} />
                        : fmtE(row.fnpc_jan)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.delta_nov_dec_pct} onChange={e => updateRow(row.matr_global_advisor, 'delta_nov_dec_pct', e.target.value)} />
                        : fmtPct(row.delta_nov_dec_pct)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.ratio_jan_dec_pct} onChange={e => updateRow(row.matr_global_advisor, 'ratio_jan_dec_pct', e.target.value)} />
                        : <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] border ${
                            row.ratio_jan_dec_pct >= data.thresholds.min_ratio_jan_dec_pct
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-gray-50 border-gray-100 text-gray-500'
                          }`}>
                            {fmtPct(row.ratio_jan_dec_pct)}
                          </span>}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
          </button>
        </div>
      )}
    </div>
  );
};

export default DecrementoFlussoNettoContainer;
